import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useSettings } from "@/components/public/shared";
import { useEnquiry } from "@/components/public/EnquiryProvider";

const LOGO = "https://customer-assets-jt897jd0.emergentagent.net/job_ethical-dining-group/artifacts/7u0c32b4_WhatsApp%20Image%202026-08-20%20at%2012.46.59%20PM.jpeg";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/catering", label: "Catering" },
  { to: "/food-services", label: "Bulk & Food Services" },
  { to: "/events", label: "Events & Experiences" },
  { to: "/hospitality", label: "Hospitality" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

function OrderOnline({ testid = "order-online", light = false }) {
  const s = useSettings();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`${testid}-trigger`} className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider transition-colors hover:text-gold ${light ? "text-white/85" : "text-ivory/85"}`}>
          Order Online <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-ivory border-subtle">
        <DropdownMenuItem asChild><a data-testid="order-zomato-button" href={s.zomato_url || "#"} target="_blank" rel="noreferrer" className="cursor-pointer">Order on Zomato</a></DropdownMenuItem>
        <DropdownMenuItem asChild><a data-testid="order-swiggy-button" href={s.swiggy_url || "#"} target="_blank" rel="noreferrer" className="cursor-pointer">Order on Swiggy</a></DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);
  const { open } = useEnquiry();
  const s = useSettings();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpenMobile(false); }, [loc.pathname]);

  const dark = !scrolled && loc.pathname === "/";
  const wa = `https://wa.me/${(s.whatsapp_number || "919884272560").replace(/\D/g, "")}`;

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      {/* Utility strip */}
      <div className="hidden bg-charcoal text-ivory md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2 lg:px-8">
          <div className="text-[11px] uppercase tracking-wider text-ivory/60">
            Pure Veg &amp; Jain-Friendly · <span className="text-gold">Chinese · Thai · Japanese · Italian · Mexican · European</span>
          </div>
          <div className="flex items-center gap-5">
            <button onClick={() => open("event")} data-testid="topbar-catering" className="text-[11px] font-semibold uppercase tracking-wider text-ivory/85 hover:text-gold">Event Catering</button>
            <span className="h-3 w-px bg-ivory/20" />
            <button onClick={() => open("bulk")} data-testid="topbar-bulk" className="text-[11px] font-semibold uppercase tracking-wider text-ivory/85 hover:text-gold">Bulk &amp; Food Services</button>
            <span className="h-3 w-px bg-ivory/20" />
            <OrderOnline />
            <span className="h-3 w-px bg-ivory/20" />
            <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ivory/85 hover:text-[#25D366]"><i className="fa-brands fa-whatsapp text-sm" /> WhatsApp</a>
            <a href={`tel:${(s.company_phone || "").replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gold"><Phone className="h-3 w-3" /> {s.company_phone}</a>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className={`transition-all duration-500 ${scrolled ? "bg-ivory/95 backdrop-blur-xl border-b border-subtle shadow-sm" : "bg-transparent"}`}>
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <span className="rounded-md bg-white/95 px-3 py-2 shadow-md ring-1 ring-gold/30">
              <img src={s.logo_url || LOGO} alt="Mee2Momos — Pure Veg & Jain-Friendly Foods" className="h-10 w-auto object-contain sm:h-12 xl:h-14" />
            </span>
          </Link>

          <div className="hidden items-center gap-4 xl:flex 2xl:gap-6">
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`whitespace-nowrap text-[12px] font-medium uppercase tracking-wider transition-colors hover:text-gold ${loc.pathname === l.to ? "text-gold" : dark ? "text-white/90" : "text-espresso/80"}`}>
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button data-testid="nav-enquire-button" onClick={() => open("event")}
              className="hidden rounded-none bg-espresso px-5 py-2.5 text-[12px] font-semibold uppercase tracking-wider text-ivory transition-colors hover:bg-gold sm:inline-block">
              Enquire Now
            </button>
            <Sheet open={openMobile} onOpenChange={setOpenMobile}>
              <SheetTrigger asChild>
                <button data-testid="nav-mobile-toggle" className={`xl:hidden ${dark ? "text-white" : "text-espresso"}`}><Menu className="h-6 w-6" /></button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] overflow-y-auto bg-ivory border-subtle">
                <div className="mb-8">
                  <span className="inline-block rounded-md bg-white px-2.5 py-1.5 ring-1 ring-gold/30">
                    <img src={s.logo_url || LOGO} alt="Mee2Momos — Pure Veg & Jain-Friendly Foods" className="h-9 w-auto object-contain" />
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  {LINKS.map((l) => (
                    <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="border-b border-subtle/60 py-3 text-sm font-medium uppercase tracking-wider text-espresso/80 hover:text-gold">
                      {l.label}
                    </Link>
                  ))}
                  <Link to="/menu" data-testid="mobile-nav-menu" className="border-b border-subtle/60 py-3 text-sm font-medium uppercase tracking-wider text-espresso/80 hover:text-gold">Menu</Link>
                </div>
                <div className="mt-6 flex flex-col gap-3">
                  <button onClick={() => open("event")} className="bg-espresso py-3 text-[12px] font-semibold uppercase tracking-wider text-ivory">Event Catering</button>
                  <button onClick={() => open("bulk")} className="border border-espresso py-3 text-[12px] font-semibold uppercase tracking-wider text-espresso">Bulk Food Services</button>
                  <a href={s.zomato_url || "#"} target="_blank" rel="noreferrer" className="bg-[#CB202D] py-3 text-center text-[12px] font-semibold uppercase tracking-wider text-white">Order on Zomato</a>
                  <a href={s.swiggy_url || "#"} target="_blank" rel="noreferrer" className="bg-[#FC8019] py-3 text-center text-[12px] font-semibold uppercase tracking-wider text-white">Order on Swiggy</a>
                  <a href={wa} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-[#25D366] py-3 text-[12px] font-semibold uppercase tracking-wider text-white"><i className="fa-brands fa-whatsapp" /> WhatsApp Us</a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
}
